import { useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { interpretInterval, isIntervalChar } from '../utils/intervalQuickSwitch'
import type { PeriodKey } from '../utils/resolution'

/** How long (ms) the prompt stays up after the last keystroke before auto-dismissing. */
const IDLE_DISMISS_MS = 1800

export interface IntervalQuickSwitchState {
  buffer: string
  label: string
  valid: boolean
}

/**
 * Quick interval switch: while the pointer is over `elementRef` (the chart),
 * typing a number (optionally a unit letter) opens a "Change interval" prompt;
 * Enter applies the matching period, Escape/idle dismisses, Backspace edits.
 *
 * Hover-gated rather than focus-gated on purpose: the chart container doubles
 * as klinecharts' pointer/wheel surface, so we must NOT attach pointer/keydown
 * listeners to it or steal focus into it — doing so wedges klinecharts' own
 * drag-pan and wheel-zoom handling (the container's wheel handler stops
 * responding). Instead we watch pointerenter/leave (passive, non-interfering)
 * to know which chart is "active", and listen for keys on `window`. Only the
 * hovered chart consumes the keystroke, so input never leaks across panels.
 */
export function useIntervalQuickSwitch(
  elementRef: RefObject<HTMLElement | null>,
  onApply: (periodKey: PeriodKey) => void,
): IntervalQuickSwitchState | null {
  const [buffer, setBuffer] = useState<string | null>(null)
  const bufferRef = useRef<string | null>(null)
  bufferRef.current = buffer
  const onApplyRef = useRef(onApply)
  onApplyRef.current = onApply
  const hoveredRef = useRef(false)

  useEffect(() => {
    const el = elementRef.current
    if (!el) return

    let idleTimer: ReturnType<typeof setTimeout> | null = null
    const clearIdle = () => {
      if (idleTimer) clearTimeout(idleTimer)
      idleTimer = null
    }
    const close = () => {
      clearIdle()
      setBuffer(null)
    }
    const armIdle = () => {
      clearIdle()
      idleTimer = setTimeout(() => setBuffer(null), IDLE_DISMISS_MS)
    }

    const onPointerEnter = () => {
      hoveredRef.current = true
    }
    const onPointerLeave = () => {
      hoveredRef.current = false
      // Dismiss any in-progress prompt when the cursor leaves this chart.
      if (bufferRef.current != null) close()
    }

    const onKeyDown = (e: KeyboardEvent) => {
      // Only the chart under the pointer responds.
      if (!hoveredRef.current) return
      if (e.ctrlKey || e.metaKey || e.altKey) return
      const target = e.target as HTMLElement
      const tag = target.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
        return
      }
      const cur = bufferRef.current

      if (e.key === 'Enter') {
        if (cur == null) return
        e.preventDefault()
        e.stopImmediatePropagation()
        const interp = interpretInterval(cur)
        if (interp?.periodKey) onApplyRef.current(interp.periodKey)
        close()
        return
      }
      if (e.key === 'Escape') {
        if (cur == null) return
        e.preventDefault()
        e.stopImmediatePropagation()
        close()
        return
      }
      if (e.key === 'Backspace') {
        if (cur == null) return
        e.preventDefault()
        e.stopImmediatePropagation()
        const next = cur.slice(0, -1)
        if (next === '') {
          close()
        } else {
          setBuffer(next)
          armIdle()
        }
        return
      }
      if (e.key.length === 1 && isIntervalChar(e.key, cur ?? '')) {
        e.preventDefault()
        e.stopImmediatePropagation()
        setBuffer((cur ?? '') + e.key)
        armIdle()
      }
    }

    el.addEventListener('pointerenter', onPointerEnter)
    el.addEventListener('pointerleave', onPointerLeave)
    window.addEventListener('keydown', onKeyDown, { capture: true })
    return () => {
      clearIdle()
      el.removeEventListener('pointerenter', onPointerEnter)
      el.removeEventListener('pointerleave', onPointerLeave)
      window.removeEventListener('keydown', onKeyDown, { capture: true })
    }
  }, [elementRef])

  if (buffer == null) return null
  const interp = interpretInterval(buffer)
  return { buffer, label: interp?.label ?? '', valid: interp?.valid ?? false }
}
